import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import type { Database } from '@/integrations/supabase/types';

type AppRole = Database["public"]["Enums"]["app_role"];

export function useUserRole() {
  const { user, loading: authLoading } = useAuth();
  const { activeRestaurant, isLoading: restaurantLoading } = useRestaurantContext();
  const [role, setRole] = useState<AppRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading || restaurantLoading) {
      setIsLoading(true);
      return;
    }

    if (!user || !activeRestaurant) {
      setRole(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchRole() {
      setIsLoading(true);

      try {
        const { data, error } = await supabase
          .from('restaurant_users')
          .select('role')
          .eq('user_id', user!.id)
          .eq('restaurant_id', activeRestaurant!.id)
          .maybeSingle();

        if (cancelled) return;

        if (error) throw error;
        setRole(data?.role ?? null);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching user role:', error);
        setRole(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchRole();

    return () => {
      cancelled = true;
    };
  }, [user, activeRestaurant, authLoading, restaurantLoading]);

  const isWaiter = role === 'waiter';
  const isCashier = role === 'cashier';
  const isManager = role === 'manager';
  const isOwner = role === 'restaurant_owner';
  const isKitchen = role === 'kitchen';
  const canAccessFullDashboard = isOwner || isManager || role === 'super_admin';
  const canAccessPDV = true; // Todos podem acessar PDV
  const canAccessReports = isOwner || isManager;
  const canAccessSettings = isOwner || isManager;
  const canAccessKDS = isOwner || isManager || isKitchen || role === 'super_admin';

  return {
    role,
    isLoading,
    isWaiter,
    isCashier,
    isManager,
    isOwner,
    isKitchen,
    canAccessFullDashboard,
    canAccessPDV,
    canAccessReports,
    canAccessSettings,
    canAccessKDS,
  };
}
